import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl, ToastNotifications } from "spark-core-dx/components";
import { Operations } from "../../../../../services/ItemServices";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import {
  buildResourceOperationQueryParams,
  buildWizardValidationError,
  getErrorSummary,
  getOperationItem,
  getOperationResult,
  getRunSectionStepInputValues,
  getSectionStepUiPatternKey,
  getValue,
  requireSuccessfulServiceResponse
} from "../resourceConfigWizardUtils";
import { InfoPair, StepField } from "./WizardStepCommon";

const OPERATION_STATE_FIELD = "AIResourceProvisioningOperation";

const PROVIDER_KIND = {
  OpenAINative: 1,
  MicrosoftFoundry: 2,
  ClaudeNative: 3
};

const RESOURCE_FAMILY = {
  MicrosoftFoundryShared: 1,
  MicrosoftFoundryDedicatedConvokeAzure: 2,
  MicrosoftFoundryDedicatedCustomerAzure: 3,
  OpenAINative: 4,
  ClaudeNative: 5
};

const PROVISIONING_ACTION = {
  AutomaticSharedPlacement: 1,
  DeployDedicated: 2,
  ConnectExisting: 3,
  ConfigureNativeProvider: 4
};

const PROVISIONING_STATUS = {
  Draft: 0,
  Planned: 1,
  PendingApproval: 2,
  Provisioning: 3,
  Provisioned: 4,
  Discovering: 5,
  Discovered: 6,
  Validating: 7,
  ReadyForActivation: 8,
  Activated: 9,
  Blocked: 10,
  Failed: 11
};

const STATUS_LABELS = Object.fromEntries(
  Object.entries(PROVISIONING_STATUS).map(([label, value]) => [value, label.replace(/([a-z])([A-Z])/g, "$1 $2")])
);

const hasReachedStatus = (status, targetStatus) => {
  const successfulProgression = [
    PROVISIONING_STATUS.Planned,
    PROVISIONING_STATUS.Provisioned,
    PROVISIONING_STATUS.Discovered,
    PROVISIONING_STATUS.ReadyForActivation,
    PROVISIONING_STATUS.Activated
  ];
  const currentIndex = successfulProgression.indexOf(status);
  const targetIndex = successfulProgression.indexOf(targetStatus);
  return currentIndex >= 0 && targetIndex >= 0 && currentIndex >= targetIndex;
};

const SKU_CONFIGURATION = {
  ConvokeAISharedStarter: ["MicrosoftFoundryShared", "FoundrySharedStarter", "1"],
  ConvokeAISharedCore: ["MicrosoftFoundryShared", "FoundrySharedCore", "1"],
  ConvokeAISharedProfessional: ["MicrosoftFoundryShared", "FoundrySharedProfessional", "1"],
  ConvokeAISharedAdvanced: ["MicrosoftFoundryShared", "FoundrySharedAdvanced", "1"],
  ConvokeAIDedicatedConvokeCore: ["MicrosoftFoundryDedicatedConvokeAzure", "FoundryDedicatedConvokeCore", "1"],
  ConvokeAIDedicatedConvokeProfessional: ["MicrosoftFoundryDedicatedConvokeAzure", "FoundryDedicatedConvokeProfessional", "1"],
  ConvokeAIDedicatedConvokeAdvanced: ["MicrosoftFoundryDedicatedConvokeAzure", "FoundryDedicatedConvokeAdvanced", "1"],
  ConvokeAIDedicatedCustomerAzure: ["MicrosoftFoundryDedicatedCustomerAzure", "", ""],
  ConvokeAIOpenAINative: ["OpenAINative", "", ""],
  ConvokeAIClaudeNative: ["ClaudeNative", "", ""]
};

const OPERATION_PATTERNS = {
  ConvokeAIProvisioning: {
    title: "Provision or connect the AI Resource",
    description: "Create and approve an immutable plan before the authorized backend performs onboarding.",
    targetStatus: PROVISIONING_STATUS.Provisioned,
    actions: [
      ["Create approved plan", "PlanConvokeAIResourceProvisioning", PROVISIONING_STATUS.Planned],
      ["Provision / Connect", "ProvisionConvokeAIResource", PROVISIONING_STATUS.Provisioned]
    ]
  },
  ConvokeAIModelAvailability: {
    title: "Discover model availability",
    description: "Read the exact provider Resource inventory. Discovery does not enable or invoke a model.",
    targetStatus: PROVISIONING_STATUS.Discovered,
    actions: [["Discover models", "DiscoverConvokeAIModels", PROVISIONING_STATUS.Discovered]]
  },
  ConvokeAIValidation: {
    title: "Validate the AI Resource",
    description: "Validate provider access, conversation and tool continuity, usage admission, and shared isolation before activation.",
    targetStatus: PROVISIONING_STATUS.ReadyForActivation,
    actions: [["Run validation", "ValidateConvokeAIResource", PROVISIONING_STATUS.ReadyForActivation]]
  },
  ConvokeAIActivation: {
    title: "Activate the AI Resource",
    description: "Activate only the exact Resource, provider profile, policy, and discovered deployments that passed review.",
    targetStatus: PROVISIONING_STATUS.Activated,
    actions: [["Activate configuration", "ActivateConvokeAIConfiguration", PROVISIONING_STATUS.Activated]]
  }
};

const APPROVAL_FIELDS = [
  {
    FieldName: "ExplicitApprovalConfirmed",
    DisplayName: "Approve this exact provisioning plan",
    Description: "Required before a backend deployment, placement, or provider connection can run.",
    FieldTypeKey: "CheckBox",
    IsRequired: true
  },
  {
    FieldName: "ApprovalNote",
    DisplayName: "Approval Note",
    Description: "Explain why this AI Resource and cost boundary are approved.",
    FieldTypeKey: "TextArea",
    IsRequired: true
  }
];

const text = (value) => String(value ?? "").trim();
const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && text(value) !== "");
const isTrue = (value) => value === true || value === 1 || text(value).toLowerCase() === "true";

const getProductResource = (wizard) => getValue(wizard, "ProductResource", "productResource") || {};

const getProductResourceKey = (wizard) => {
  const resource = getProductResource(wizard);
  const resourceConfiguration = getValue(wizard, "ResourceConfiguration", "resourceConfiguration") || {};
  return text(getValue(resource, "ProductResourceKey", "productResourceKey")
    || getValue(wizard, "ProductResourceKey", "productResourceKey")
    || getValue(resourceConfiguration, "ProductResourceKey", "productResourceKey"));
};

const getSkuConfiguration = (wizard) => {
  const productResource = getProductResource(wizard);
  const productResourceKey = getProductResourceKey(wizard);
  const configured = SKU_CONFIGURATION[productResourceKey] || ["", "", ""];
  return {
    productResourceKey,
    familyKey: text(getValue(productResource, "CatalogViewGroupKey", "catalogViewGroupKey") || configured[0]),
    packageKey: text(getValue(productResource, "CatalogPackageKey", "catalogPackageKey") || configured[1]),
    packageVersion: text(getValue(productResource, "CatalogPackageVersion", "catalogPackageVersion") || configured[2])
  };
};

const getProviderKind = (familyKey) => {
  if (familyKey === "OpenAINative") return PROVIDER_KIND.OpenAINative;
  if (familyKey === "ClaudeNative") return PROVIDER_KIND.ClaudeNative;
  return PROVIDER_KIND.MicrosoftFoundry;
};

const getDefaultConnectionMode = (familyKey) => {
  if (familyKey === "MicrosoftFoundryShared") return "Automated";
  if (familyKey === "MicrosoftFoundryDedicatedConvokeAzure") return "Deploy";
  return "Existing";
};

const getProvisioningAction = (familyKey, connectionMode) => {
  if (familyKey === "MicrosoftFoundryShared") return PROVISIONING_ACTION.AutomaticSharedPlacement;
  if (familyKey === "MicrosoftFoundryDedicatedConvokeAzure") return PROVISIONING_ACTION.DeployDedicated;
  if (familyKey === "MicrosoftFoundryDedicatedCustomerAzure") {
    return connectionMode === "Existing" ? PROVISIONING_ACTION.ConnectExisting : PROVISIONING_ACTION.DeployDedicated;
  }
  return PROVISIONING_ACTION.ConfigureNativeProvider;
};

const getProviderFieldNames = (familyKey) => {
  if (["OpenAINative", "ClaudeNative"].includes(familyKey)) {
    return new Set(["ResourceFamily", "ConnectionMode", "SecretReference"]);
  }
  if (familyKey === "MicrosoftFoundryShared") {
    return new Set(["ResourceFamily", "ConnectionMode", "AzureRegion"]);
  }
  if (familyKey === "MicrosoftFoundryDedicatedConvokeAzure") {
    return new Set(["ResourceFamily", "ConnectionMode", "AzureRegion"]);
  }
  return new Set([
    "ResourceFamily",
    "ConnectionMode",
    "ProjectEndpoint",
    "AzureTenantId",
    "AzureSubscriptionId",
    "AzureResourceGroupName",
    "AzureRegion",
    "FoundryResourceName",
    "FoundryProjectName",
    "RuntimeIdentityReference"
  ]);
};

const getConnectionModeDataJson = (familyKey) => {
  const options = familyKey === "MicrosoftFoundryDedicatedCustomerAzure"
    ? [
        { Text: "Connect Existing", Value: "Existing" },
        { Text: "Deploy New Dedicated", Value: "Deploy" }
      ]
    : [{
        Text: familyKey === "MicrosoftFoundryShared"
          ? "Automated Onboarding"
          : familyKey === "MicrosoftFoundryDedicatedConvokeAzure"
            ? "Deploy New Dedicated"
            : "Connect Existing",
        Value: getDefaultConnectionMode(familyKey)
      }];
  return JSON.stringify(options);
};

const getStoredOperation = (context) => {
  const current = getValue(context.values, OPERATION_STATE_FIELD, "aiResourceProvisioningOperation");
  if (current && typeof current === "object") return current;

  const locations = [
    ["validate", "validate-ai-resource"],
    ["models", "model-availability"],
    ["provision", "provision-ai-resource"]
  ];
  for (const [stepKey, sectionStepKey] of locations) {
    const values = getRunSectionStepInputValues(context.wizard, stepKey, sectionStepKey);
    const operation = getValue(values, OPERATION_STATE_FIELD, "aiResourceProvisioningOperation");
    if (operation && typeof operation === "object") return operation;
  }
  return {};
};

const buildOperation = (context, operation, overrideValues = {}) => {
  const wizard = context.wizard || {};
  const resourceConfiguration = getValue(wizard, "ResourceConfiguration", "resourceConfiguration") || {};
  const providerValues = getRunSectionStepInputValues(wizard, "provider", "provider-config");
  const currentProviderValues = getSectionStepUiPatternKey(context.sectionStep) === "ConvokeAIProviderConfigurator"
    ? context.values || {}
    : {};
  const sku = getSkuConfiguration(wizard);
  const familyKey = sku.familyKey;
  const connectionMode = text(firstValue(
    overrideValues.ConnectionMode,
    currentProviderValues.ConnectionMode,
    providerValues.ConnectionMode,
    getDefaultConnectionMode(familyKey)
  ));
  const readConfiguredValue = (fieldName) => firstValue(
    overrideValues[fieldName],
    currentProviderValues[fieldName],
    providerValues[fieldName],
    operation[fieldName]
  );

  return {
    ...operation,
    ProvisioningStatus: Number(firstValue(
      getValue(operation, "ProvisioningStatus", "provisioningStatus"),
      PROVISIONING_STATUS.Draft
    )),
    ResourceConfigurationId: text(firstValue(
      getValue(wizard, "ResourceConfigurationId", "resourceConfigurationId"),
      getValue(resourceConfiguration, "_id", "id", "UniqueId", "uniqueId"),
      operation.ResourceConfigurationId
    )),
    ProductResourceKey: sku.productResourceKey,
    EnvironmentKey: text(firstValue(getValue(wizard, "EnvironmentKey", "environmentKey"), operation.EnvironmentKey)),
    ProviderKind: getProviderKind(familyKey),
    ResourceFamily: RESOURCE_FAMILY[familyKey] || 0,
    ProvisioningAction: getProvisioningAction(familyKey, connectionMode),
    Region: text(readConfiguredValue("AzureRegion")),
    ModelPackageKey: sku.packageKey,
    ModelPackageVersion: sku.packageVersion,
    AzureTenantId: text(readConfiguredValue("AzureTenantId")),
    AzureSubscriptionId: text(readConfiguredValue("AzureSubscriptionId")),
    AzureResourceGroupName: text(readConfiguredValue("AzureResourceGroupName")),
    FoundryResourceName: text(readConfiguredValue("FoundryResourceName")),
    FoundryProjectName: text(readConfiguredValue("FoundryProjectName")),
    ProjectEndpoint: text(readConfiguredValue("ProjectEndpoint")),
    RuntimeIdentityReference: text(readConfiguredValue("RuntimeIdentityReference")),
    SecretReference: text(readConfiguredValue("SecretReference")),
    ExplicitApprovalConfirmed: isTrue(overrideValues.ExplicitApprovalConfirmed
      ?? operation.ExplicitApprovalConfirmed
      ?? false),
    ApprovalNote: text(firstValue(overrideValues.ApprovalNote, operation.ApprovalNote))
  };
};

const ConfiguratorStep = ({ context, onChange, onSave, onContinue, registerStepController }) => {
  const patternKey = getSectionStepUiPatternKey(context.sectionStep);
  const sku = getSkuConfiguration(context.wizard);
  const isProviderStep = patternKey === "ConvokeAIProviderConfigurator";
  const defaults = useMemo(() => isProviderStep ? {
    ResourceFamily: sku.familyKey,
    ConnectionMode: getDefaultConnectionMode(sku.familyKey)
  } : {
    MaximumDataClassification: 1,
    FileUploadEnabled: false,
    ReadOnlyWebEnabled: false
  }, [isProviderStep, sku.familyKey]);
  const fields = useMemo(() => {
    if (!isProviderStep) return context.fields;
    const allowedFields = getProviderFieldNames(sku.familyKey);
    return context.fields
      .filter((field) => allowedFields.has(getValue(field, "FieldName", "fieldName")))
      .map((field) => getValue(field, "FieldName", "fieldName") === "ConnectionMode"
        ? { ...field, DataJson: getConnectionModeDataJson(sku.familyKey) }
        : field);
  }, [context.fields, isProviderStep, sku.familyKey]);
  const valuesRef = useRef({});
  const resolvedValues = useMemo(() => fields.reduce((result, field) => {
    const fieldName = getValue(field, "FieldName", "fieldName");
    result[fieldName] = context.values?.[fieldName] ?? defaults[fieldName] ?? "";
    return result;
  }, {}), [context.values, defaults, fields]);
  valuesRef.current = resolvedValues;

  useEffect(() => {
    registerStepController?.({
      prepareSave: async (currentValues = {}) => ({ ...valuesRef.current, ...currentValues }),
      prepareContinue: async (currentValues = {}) => ({ ...valuesRef.current, ...currentValues })
    });
    return () => registerStepController?.(null);
  }, [registerStepController]);

  return (
    <>
      <div className="resource-wizard__fields">
        {fields.map((field) => {
          const fieldName = getValue(field, "FieldName", "fieldName");
          return (
            <StepField
              key={fieldName}
              field={field}
              value={resolvedValues[fieldName]}
              disabled={context.readOnly || (isProviderStep && (
                fieldName === "ResourceFamily"
                || (fieldName === "ConnectionMode" && sku.familyKey !== "MicrosoftFoundryDedicatedCustomerAzure")
              ))}
              onChange={onChange}
            />
          );
        })}
      </div>
      <div className="resource-wizard__linked-step-actions">
        <div />
        <div className="resource-wizard__linked-step-actions-right">
          <ButtonControl type="standard-small" onClick={() => onSave?.()} disabled={context.readOnly || context.saving} loading={context.saving}>
            Save
          </ButtonControl>
          <ButtonControl type="ok-small" onClick={() => onContinue?.()} disabled={context.readOnly || context.saving} loading={context.saving}>
            Save &amp; Continue
          </ButtonControl>
        </div>
      </div>
    </>
  );
};

const OperationStep = ({ context, onChange, onSave, onContinue, registerStepController }) => {
  const resourceDomain = useResourceDomainContext();
  const manager = resourceDomain?.managers?.aiResourceProvisioningOperation;
  const coreState = resourceDomain?.coreState;
  const patternKey = getSectionStepUiPatternKey(context.sectionStep);
  const definition = OPERATION_PATTERNS[patternKey];
  const [operation, setOperation] = useState(() => buildOperation(context, getStoredOperation(context)));
  const [approvalValues, setApprovalValues] = useState(() => ({
    ExplicitApprovalConfirmed: isTrue(operation.ExplicitApprovalConfirmed),
    ApprovalNote: operation.ApprovalNote || ""
  }));
  const [runningOperation, setRunningOperation] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [operationError, setOperationError] = useState("");
  const operationRef = useRef(operation);
  operationRef.current = operation;

  const storeOperation = useCallback((nextOperation) => {
    operationRef.current = nextOperation;
    setOperation(nextOperation);
    onChange?.(OPERATION_STATE_FIELD, nextOperation);
  }, [onChange]);

  useEffect(() => {
    const stored = getStoredOperation(context);
    if (!getValue(context.values, OPERATION_STATE_FIELD, "aiResourceProvisioningOperation") && Object.keys(stored).length) {
      storeOperation(buildOperation(context, stored));
    }
  }, [context, storeOperation]);

  useEffect(() => {
    registerStepController?.({
      skipStepInputValidation: true,
      prepareSave: async (currentValues = {}) => ({
        ...currentValues,
        [OPERATION_STATE_FIELD]: operationRef.current
      }),
      prepareContinue: async (currentValues = {}) => {
        const status = Number(getValue(operationRef.current, "ProvisioningStatus", "provisioningStatus"));
        if (!hasReachedStatus(status, definition.targetStatus)) {
          throw buildWizardValidationError(`Complete ${definition.title.toLowerCase()} before continuing.`);
        }
        return { ...currentValues, [OPERATION_STATE_FIELD]: operationRef.current };
      }
    });
    return () => registerStepController?.(null);
  }, [definition.targetStatus, definition.title, registerStepController]);

  const runOperation = useCallback(async (operationName, expectedStatus) => {
    if (!manager?.RunOperation) {
      setOperationError("The AI Resource provisioning service is not available.");
      return;
    }

    const item = buildOperation(context, operationRef.current, approvalValues);
    setRunningOperation(operationName);
    setOperationError("");
    setToastMessage("");
    try {
      const response = await manager.RunOperation({
        operationName: Operations.AIResourceProvisioningOperationItem?.[operationName] || operationName,
        item,
        queryParams: buildResourceOperationQueryParams({ wizard: context.wizard, item }),
        isItem: true,
        isWarning: true,
        supressAllErrors: false
      });
      const returnedOperation = getOperationItem(response) || getOperationResult(response);
      if (returnedOperation && typeof returnedOperation === "object") {
        const normalizedOperation = {
          ...buildOperation(context, returnedOperation),
          ...returnedOperation
        };
        storeOperation(normalizedOperation);
      }
      requireSuccessfulServiceResponse(response, `${definition.title} failed.`, coreState);
      if (!returnedOperation || typeof returnedOperation !== "object") {
        throw new Error("The operation did not return its updated provisioning state.");
      }
      const nextOperation = {
        ...buildOperation(context, returnedOperation),
        ...returnedOperation
      };
      const returnedStatus = Number(getValue(nextOperation, "ProvisioningStatus", "provisioningStatus"));
      if (!hasReachedStatus(returnedStatus, expectedStatus)) {
        throw new Error(`The backend returned ${STATUS_LABELS[returnedStatus] || "an unknown state"}; ${STATUS_LABELS[expectedStatus]} is required.`);
      }
      storeOperation(nextOperation);
      setToastMessage(`${definition.title} completed.`);
    } catch (error) {
      setOperationError(getErrorSummary(error, `${definition.title} failed.`));
    } finally {
      setRunningOperation("");
    }
  }, [approvalValues, context, coreState, definition.title, manager, storeOperation]);

  const status = Number(getValue(operation, "ProvisioningStatus", "provisioningStatus"));
  const discoveredDeploymentIds = getValue(operation, "DiscoveredDeploymentIds", "discoveredDeploymentIds") || [];
  const planSteps = getValue(operation, "PlanSteps", "planSteps") || [];
  const lastErrorCode = text(getValue(operation, "LastErrorCode", "lastErrorCode"));
  const provisioningPattern = patternKey === "ConvokeAIProvisioning";
  const isActionAllowed = (operationName) => {
    if (operationName === "PlanConvokeAIResourceProvisioning") {
      return [PROVISIONING_STATUS.Draft, PROVISIONING_STATUS.PendingApproval, PROVISIONING_STATUS.Planned].includes(status)
        && isTrue(approvalValues.ExplicitApprovalConfirmed)
        && Boolean(text(approvalValues.ApprovalNote));
    }
    if (operationName === "ProvisionConvokeAIResource") {
      return status === PROVISIONING_STATUS.Planned
        || (status === PROVISIONING_STATUS.Failed && lastErrorCode === "ProvisioningFailed");
    }
    if (operationName === "DiscoverConvokeAIModels") {
      return [PROVISIONING_STATUS.Provisioned, PROVISIONING_STATUS.Discovered].includes(status)
        || (status === PROVISIONING_STATUS.Blocked && lastErrorCode === "NoDeploymentsDiscovered")
        || (status === PROVISIONING_STATUS.Failed && lastErrorCode === "DiscoveryFailed");
    }
    if (operationName === "ValidateConvokeAIResource") {
      return [PROVISIONING_STATUS.Discovered, PROVISIONING_STATUS.ReadyForActivation].includes(status)
        || (status === PROVISIONING_STATUS.Blocked && lastErrorCode !== "NoDeploymentsDiscovered")
        || (status === PROVISIONING_STATUS.Failed && lastErrorCode === "ValidationFailed");
    }
    if (operationName === "ActivateConvokeAIConfiguration") {
      return [PROVISIONING_STATUS.ReadyForActivation, PROVISIONING_STATUS.Activated].includes(status);
    }
    return false;
  };

  return (
    <div className="resource-wizard__pattern">
      {toastMessage ? <ToastNotifications position="top" message={toastMessage} /> : null}
      <h3>{definition.title}</h3>
      <p>{definition.description}</p>

      <div className="resource-wizard__pattern-facts">
        <InfoPair label="Status" value={STATUS_LABELS[status] || "Draft"} />
        <InfoPair label="Plan version" value={getValue(operation, "PlanVersion", "planVersion")} />
        <InfoPair label="Plan hash" value={getValue(operation, "PlanHash", "planHash")} />
        <InfoPair label="Provider profile" value={getValue(operation, "ProviderProfileId", "providerProfileId")} />
        <InfoPair label="Discovered deployments" value={discoveredDeploymentIds.length ? discoveredDeploymentIds.join(", ") : "None"} />
        <InfoPair label="Validation" value={getValue(operation, "ValidationSummary", "validationSummary")} />
      </div>

      {provisioningPattern ? (
        <div className="resource-wizard__fields">
          {APPROVAL_FIELDS.map((field) => (
            <StepField
              key={field.FieldName}
              field={field}
              value={approvalValues[field.FieldName]}
              disabled={context.readOnly
                || Boolean(runningOperation)
                || ![PROVISIONING_STATUS.Draft, PROVISIONING_STATUS.PendingApproval].includes(status)}
              onChange={(fieldName, value) => setApprovalValues((current) => ({ ...current, [fieldName]: value }))}
            />
          ))}
        </div>
      ) : null}

      {planSteps.length ? (
        <div className="resource-wizard__pattern-facts">
          <strong>Approved plan steps</strong>
          <ol>{planSteps.map((step) => <li key={step}>{step}</li>)}</ol>
        </div>
      ) : null}

      <div className="resource-wizard__linked-step-actions">
        <div className="resource-wizard__linked-step-actions-left">
          {definition.actions.map(([label, operationName, expectedStatus]) => (
            <ButtonControl
              key={operationName}
              type={operationName === "ProvisionConvokeAIResource" || operationName === "ActivateConvokeAIConfiguration" ? "ok-small" : "standard-small"}
              onClick={() => runOperation(operationName, expectedStatus)}
              disabled={context.readOnly || Boolean(runningOperation) || !isActionAllowed(operationName)}
              loading={runningOperation === operationName}
            >
              {label}
            </ButtonControl>
          ))}
        </div>
        <div className="resource-wizard__linked-step-actions-right">
          <ButtonControl type="standard-small" onClick={() => onSave?.()} disabled={context.readOnly || context.saving || Boolean(runningOperation)}>
            Save
          </ButtonControl>
          <ButtonControl type="ok-small" onClick={() => onContinue?.()} disabled={context.readOnly || context.saving || Boolean(runningOperation)}>
            Save &amp; Continue
          </ButtonControl>
        </div>
      </div>

      <DialogControl
        openDialog={Boolean(operationError)}
        title="AI Resource operation failed"
        okText="Close"
        hideCancelButton
        onOk={() => setOperationError("")}
        onX={() => setOperationError("")}
      >
        <p>{operationError}</p>
      </DialogControl>
    </div>
  );
};

const ConvokeAIResourceStep = (props) => {
  const patternKey = getSectionStepUiPatternKey(props.context.sectionStep);
  return OPERATION_PATTERNS[patternKey]
    ? <OperationStep {...props} />
    : <ConfiguratorStep {...props} />;
};

export default ConvokeAIResourceStep;
